/**
 * AdminGovernancaTemposPage — Gestão de Configuração de Cargos (Motor Temporal V18)
 *
 * Tabela de cargos com: FTE, Disponibilidade%, WIP Máx., Custo/h, Sizing.
 * Drawer lateral para configuração da Semana Padrão (5 dias × 4 blocos).
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Pencil, Check, X, Plus, Clock, AlertTriangle, CalendarDays } from 'lucide-react';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import {
  listCargos,
  createCargo,
  updateCargo,
  deleteCargo,
  getCargoWeekBlocks,
  upsertCargoWeekBlock,
  DEFAULT_SIZING,
  type CargoConfig,
  type SizingMap,
  type CargoWeekBlock,
} from '../../../services/capacidadeService';

// ── Types ─────────────────────────────────────────────────────

interface EditState {
  id: string;
  name: string;
  fteBase: string;
  availabilityPct: string;
  wipMax: string;
  custoHora: string;
  sizingP: string;
  sizingM: string;
  sizingG: string;
  sizingGG: string;
  notes: string;
}

interface CreateState {
  name: string;
  fteBase: string;
  availabilityPct: string;
  wipMax: string;
  custoHora: string;
  sizingP: string;
  sizingM: string;
  sizingG: string;
  sizingGG: string;
}

const BLANK_CREATE: CreateState = {
  name: '',
  fteBase: '1',
  availabilityPct: '80',
  wipMax: '8',
  custoHora: '0',
  sizingP: String(DEFAULT_SIZING.P),
  sizingM: String(DEFAULT_SIZING.M),
  sizingG: String(DEFAULT_SIZING.G),
  sizingGG: String(DEFAULT_SIZING.GG),
};

function toSizing(s: Pick<EditState | CreateState, 'sizingP' | 'sizingM' | 'sizingG' | 'sizingGG'>): SizingMap {
  return {
    P: Number(s.sizingP) || DEFAULT_SIZING.P,
    M: Number(s.sizingM) || DEFAULT_SIZING.M,
    G: Number(s.sizingG) || DEFAULT_SIZING.G,
    GG: Number(s.sizingGG) || DEFAULT_SIZING.GG,
  };
}

// ── Semana Padrão Drawer ───────────────────────────────────────

const DAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'];
const BLOCK_LABELS = ['Manhã 1', 'Manhã 2', 'Tarde 1', 'Tarde 2'];
const ACTIVITY_TYPES = ['core', 'gestao', 'externo', 'buffer'] as const;
const ACTIVITY_COLORS: Record<string, string> = {
  core: 'bg-blue-100/60 text-blue-700 border-blue-200',
  gestao: 'bg-amber-100/60 text-amber-700 border-amber-200',
  externo: 'bg-emerald-100/60 text-emerald-700 border-emerald-200',
  buffer: 'bg-slate-100/60 text-slate-500 border-slate-200',
};

interface StandardWeekDrawerProps {
  cargo: CargoConfig;
  organizationId: string;
  onClose: () => void;
}

const StandardWeekDrawer: React.FC<StandardWeekDrawerProps> = ({ cargo, organizationId, onClose }) => {
  const qc = useQueryClient();

  const blocksQuery = useQuery<CargoWeekBlock[]>({
    queryKey: ['cargo-week-blocks', cargo.id],
    queryFn: () => getCargoWeekBlocks(cargo.id),
  });

  const upsertMut = useMutation({
    mutationFn: (block: Omit<CargoWeekBlock, 'id'> & { organizationId: string }) =>
      upsertCargoWeekBlock(block),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cargo-week-blocks', cargo.id] }),
  });

  const blocks = blocksQuery.data ?? [];

  const getBlock = useCallback(
    (day: number, block: number): CargoWeekBlock | undefined =>
      blocks.find(b => b.dayOfWeek === day && b.block === block),
    [blocks],
  );

  const cycleActivity = useCallback(
    (day: number, blockNum: number) => {
      const current = getBlock(day, blockNum);
      const currentIdx = current ? ACTIVITY_TYPES.indexOf(current.activityType) : -1;
      const next = ACTIVITY_TYPES[(currentIdx + 1) % ACTIVITY_TYPES.length];
      upsertMut.mutate({
        cargoId: cargo.id,
        organizationId,
        dayOfWeek: day as 0 | 1 | 2 | 3 | 4,
        block: blockNum as 1 | 2 | 3 | 4,
        activityType: next,
        durationMinutes: 120,
      });
    },
    [cargo.id, organizationId, getBlock, upsertMut],
  );

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-[440px] shadow-2xl bg-[var(--bg-surface)] border-l border-[var(--border-default)]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-default)] shrink-0">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-[var(--text-muted)]" />
          <span className="text-[12px] font-semibold text-[var(--text-primary)]">Semana Padrão</span>
          <span className="text-[11px] text-[var(--text-muted)]">— {cargo.name}</span>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-[var(--bg-elevated)] text-[var(--text-muted)]">
          <X size={14} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[var(--border-default)] shrink-0">
        {ACTIVITY_TYPES.map(t => (
          <span key={t} className={`text-[10px] px-2 py-0.5 rounded-sm border ${ACTIVITY_COLORS[t]}`}>
            {t}
          </span>
        ))}
        <span className="text-[10px] text-[var(--text-muted)] ml-auto">clique para ciclar tipo</span>
      </div>

      {/* Matrix */}
      <div className="flex-1 overflow-auto p-4">
        {blocksQuery.isLoading && <ModuleLoadingState label="Carregando semana" />}
        {!blocksQuery.isLoading && (
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr>
                <th className="text-left py-1 pr-3 text-[var(--text-muted)] font-medium w-[70px]">Bloco</th>
                {DAY_LABELS.map(d => (
                  <th key={d} className="text-center py-1 text-[var(--text-muted)] font-medium">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BLOCK_LABELS.map((blockLabel, blockIdx) => (
                <tr key={blockIdx} className="border-t border-[var(--border-default)]">
                  <td className="py-2 pr-3 text-[var(--text-secondary)] text-[10px] whitespace-nowrap">{blockLabel}</td>
                  {DAY_LABELS.map((_, dayIdx) => {
                    const b = getBlock(dayIdx, blockIdx + 1);
                    const type = b?.activityType ?? null;
                    return (
                      <td key={dayIdx} className="py-1 px-1 text-center">
                        <button
                          type="button"
                          title={type ? `${type} — clique para mudar` : 'Clique para definir'}
                          onClick={() => cycleActivity(dayIdx, blockIdx + 1)}
                          disabled={upsertMut.isPending}
                          className={`w-full h-[32px] rounded-sm border text-[10px] font-medium transition-colors ${
                            type ? ACTIVITY_COLORS[type] : 'bg-[var(--bg-elevated)] border-[var(--border-default)] text-[var(--text-muted)]'
                          }`}
                        >
                          {type ?? '—'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-[var(--border-default)] shrink-0 text-[10px] text-[var(--text-muted)]">
        Cada bloco representa 2h. Core = trabalho operacional. Gestão = reuniões/reviews. Externo = fora do escritório. Buffer = margem.
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────

interface AdminGovernancaTemposPageProps {
  externalShowCreate?: boolean;
  onCreateDone?: () => void;
}


export const AdminGovernancaTemposPage: React.FC<AdminGovernancaTemposPageProps> = ({
  externalShowCreate,
  onCreateDone,
}) => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const qc = useQueryClient();

  const [showCreate, setShowCreate] = useState(Boolean(externalShowCreate));
  const [create, setCreate] = useState<CreateState>(BLANK_CREATE);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [weekDrawerCargo, setWeekDrawerCargo] = useState<CargoConfig | null>(null);

  const cargosQuery = useQuery({
    queryKey: ['admin-cargos', orgId],
    enabled: Boolean(orgId),
    queryFn: () => listCargos(orgId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-cargos', orgId] });
  };

  const createMut = useMutation({
    mutationFn: () =>
      createCargo(orgId, {
        name: create.name.trim(),
        fteBase: Number(create.fteBase) || 1,
        availabilityPct: Number(create.availabilityPct) || 80,
        wipMax: Number(create.wipMax) || 8,
        custoHora: Number(create.custoHora) || 0,
        sizingHours: toSizing(create),
        notes: null,
      }),
    onSuccess: () => {
      invalidate();
      setCreate(BLANK_CREATE);
      setShowCreate(false);
      onCreateDone?.();
    },
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!edit) return Promise.resolve();
      return updateCargo(edit.id, orgId, {
        name: edit.name.trim(),
        fteBase: Number(edit.fteBase) || 1,
        availabilityPct: Number(edit.availabilityPct) || 80,
        wipMax: Number(edit.wipMax) || 8,
        custoHora: Number(edit.custoHora) || 0,
        sizingHours: toSizing(edit),
        notes: edit.notes || null,
      });
    },
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      setEdit(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteCargo(id, orgId),
    onSuccess: invalidate,
  });

  const startEdit = (c: CargoConfig) => {
    setEditingId(c.id);
    setEdit({
      id: c.id,
      name: c.name,
      fteBase: String(c.fteBase),
      availabilityPct: String(c.availabilityPct),
      wipMax: String(c.wipMax),
      custoHora: String(c.custoHora),
      sizingP: String(c.sizingHours.P),
      sizingM: String(c.sizingHours.M),
      sizingG: String(c.sizingHours.G),
      sizingGG: String(c.sizingHours.GG),
      notes: c.notes ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEdit(null);
  };

  const cargos: CargoConfig[] = cargosQuery.data ?? [];

  return (
    <AdminConsoleLayout>
    <RequireCapability capability="admin.access" realRoleOnly>
      <div className="enterprise-page h-full flex flex-col overflow-hidden">
        {/* Semana Padrão Drawer */}
        {weekDrawerCargo && (
          <StandardWeekDrawer
            cargo={weekDrawerCargo}
            organizationId={orgId}
            onClose={() => setWeekDrawerCargo(null)}
          />
        )}

        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-default)] shrink-0 bg-[var(--bg-surface)]">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[var(--text-muted)]" />
            <span className="text-[12px] font-medium text-[var(--text-primary)]">
              Configuração de Cargos — Motor Temporal
            </span>
            <span className="text-[11px] text-[var(--text-muted)] ml-1">
              ({cargos.length} cargos)
            </span>
          </div>
          <button
            type="button"
            className="enterprise-btn-primary text-[11px] h-[26px] px-2 flex items-center gap-1"
            onClick={() => setShowCreate(v => !v)}
          >
            <Plus size={12} />
            Novo Cargo
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="px-4 py-2 border-b border-[var(--border-default)] shrink-0 bg-[var(--bg-elevated)]">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Nome *</span>
                <input
                  className="enterprise-input text-[12px] h-[28px] w-[160px]"
                  placeholder="Ex: Analista Sênior"
                  value={create.name}
                  onChange={e => setCreate(p => ({ ...p, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">FTE Base</span>
                <input
                  type="number"
                  min="0.1" max="3" step="0.1"
                  className="enterprise-input text-[12px] h-[28px] w-[72px]"
                  value={create.fteBase}
                  onChange={e => setCreate(p => ({ ...p, fteBase: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Disponib. %</span>
                <input
                  type="number"
                  min="0" max="100" step="1"
                  className="enterprise-input text-[12px] h-[28px] w-[72px]"
                  value={create.availabilityPct}
                  onChange={e => setCreate(p => ({ ...p, availabilityPct: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">WIP Máx.</span>
                <input
                  type="number"
                  min="1" max="50"
                  className="enterprise-input text-[12px] h-[28px] w-[64px]"
                  value={create.wipMax}
                  onChange={e => setCreate(p => ({ ...p, wipMax: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">R$/h</span>
                <input
                  type="number"
                  min="0" step="0.01"
                  className="enterprise-input text-[12px] h-[28px] w-[80px]"
                  value={create.custoHora}
                  onChange={e => setCreate(p => ({ ...p, custoHora: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">P h / M h / G h / GG h</span>
                <div className="flex items-center gap-1">
                  {(['sizingP', 'sizingM', 'sizingG', 'sizingGG'] as const).map(key => (
                    <input
                      key={key}
                      type="number"
                      min="0.5"
                      className="enterprise-input text-[12px] h-[28px] w-[52px]"
                      value={create[key]}
                      onChange={e => setCreate(p => ({ ...p, [key]: e.target.value }))}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1 mt-auto">
                <button
                  type="button"
                  className="enterprise-btn-primary text-[11px] h-[28px] px-3"
                  disabled={!create.name.trim() || createMut.isPending}
                  onClick={() => createMut.mutate()}
                >
                  {createMut.isPending ? 'Criando...' : 'Criar'}
                </button>
                <button
                  type="button"
                  className="enterprise-btn text-[11px] h-[28px] px-2"
                  onClick={() => { setShowCreate(false); setCreate(BLANK_CREATE); }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {cargosQuery.isPending && <ModuleLoadingState label="Carregando cargos" />}
          {cargosQuery.isError && (
            <div className="flex flex-col items-center justify-center h-40 gap-2 text-red-500">
              <AlertTriangle size={20} />
              <span className="text-[12px]">Erro ao carregar cargos.</span>
            </div>
          )}
          {!cargosQuery.isPending && !cargosQuery.isError && (
            <>
              {cargos.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2 text-[var(--text-muted)]">
                <AlertTriangle size={20} />
                <span className="text-[12px]">Nenhum cargo configurado.</span>
                <span className="text-[11px]">Adicione um cargo para ativar o motor de disponibilidade.</span>
              </div>
            ) : (
              <table className="w-full text-[12px] border-collapse">
                <thead>
                  <tr className="border-b border-[var(--border-default)] bg-[var(--bg-surface)]">
                    <th className="text-left px-4 py-2 font-medium text-[var(--text-muted)] w-[200px]">Cargo</th>
                    <th className="text-center px-3 py-2 font-medium text-[var(--text-muted)] w-[72px]">FTE</th>
                    <th className="text-center px-3 py-2 font-medium text-[var(--text-muted)] w-[88px]">Disponib. %</th>
                    <th className="text-center px-3 py-2 font-medium text-[var(--text-muted)] w-[72px]">WIP Máx.</th>
                    <th className="text-center px-3 py-2 font-medium text-[var(--text-muted)] w-[80px]">R$/h</th>
                    <th className="text-center px-3 py-2 font-medium text-[var(--text-muted)] w-[200px]">Sizing (P / M / G / GG h)</th>
                    <th className="text-right px-4 py-2 font-medium text-[var(--text-muted)] w-[110px]">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {cargos.map(cargo => {
                    const isEditing = editingId === cargo.id && edit !== null;
                    return (
                      <tr
                        key={cargo.id}
                        className="border-b border-[var(--border-default)] hover:bg-[var(--bg-elevated)] transition-colors"
                      >
                        {/* Name */}
                        <td className="px-4 py-2">
                          {isEditing ? (
                            <input
                              className="enterprise-input text-[12px] h-[26px] w-full"
                              value={edit!.name}
                              onChange={e => setEdit(p => p && ({ ...p, name: e.target.value }))}
                              autoFocus
                            />
                          ) : (
                            <span className="text-[var(--text-primary)] font-medium">{cargo.name}</span>
                          )}
                        </td>
                        {/* FTE */}
                        <td className="px-3 py-2 text-center">
                          {isEditing ? (
                            <input
                              type="number" min="0.1" max="3" step="0.1"
                              className="enterprise-input text-[12px] h-[26px] w-full text-center"
                              value={edit!.fteBase}
                              onChange={e => setEdit(p => p && ({ ...p, fteBase: e.target.value }))}
                            />
                          ) : (
                            <span>{cargo.fteBase}</span>
                          )}
                        </td>
                        {/* Availability */}
                        <td className="px-3 py-2 text-center">
                          {isEditing ? (
                            <input
                              type="number" min="0" max="100"
                              className="enterprise-input text-[12px] h-[26px] w-full text-center"
                              value={edit!.availabilityPct}
                              onChange={e => setEdit(p => p && ({ ...p, availabilityPct: e.target.value }))}
                            />
                          ) : (
                            <span>{cargo.availabilityPct}%</span>
                          )}
                        </td>
                        {/* WIP Max */}
                        <td className="px-3 py-2 text-center">
                          {isEditing ? (
                            <input
                              type="number" min="1" max="50"
                              className="enterprise-input text-[12px] h-[26px] w-full text-center"
                              value={edit!.wipMax}
                              onChange={e => setEdit(p => p && ({ ...p, wipMax: e.target.value }))}
                            />
                          ) : (
                            <span>{cargo.wipMax}</span>
                          )}
                        </td>
                        {/* Custo/hora */}
                        <td className="px-3 py-2 text-center">
                          {isEditing ? (
                            <input
                              type="number" min="0" step="0.01"
                              className="enterprise-input text-[12px] h-[26px] w-full text-center"
                              value={edit!.custoHora}
                              onChange={e => setEdit(p => p && ({ ...p, custoHora: e.target.value }))}
                            />
                          ) : (
                            <span className="text-[var(--text-secondary)]">
                              {Number(cargo.custoHora).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          )}
                        </td>
                        {/* Sizing */}
                        <td className="px-3 py-2 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              {(['sizingP', 'sizingM', 'sizingG', 'sizingGG'] as const).map(key => (
                                <input
                                  key={key}
                                  type="number" min="0.5"
                                  className="enterprise-input text-[12px] h-[26px] w-[44px] text-center"
                                  value={edit![key]}
                                  onChange={e => setEdit(p => p && ({ ...p, [key]: e.target.value }))}
                                />
                              ))}
                            </div>
                          ) : (
                            <span className="text-[var(--text-secondary)]">
                              {cargo.sizingHours.P} / {cargo.sizingHours.M} / {cargo.sizingHours.G} / {cargo.sizingHours.GG}
                            </span>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-2 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                title="Salvar"
                                className="p-1 rounded-sm hover:bg-emerald-100/20 text-emerald-600"
                                disabled={updateMut.isPending}
                                onClick={() => updateMut.mutate()}
                              >
                                <Check size={13} />
                              </button>
                              <button
                                type="button"
                                title="Cancelar"
                                className="p-1 rounded-sm hover:bg-[var(--bg-elevated)] text-[var(--text-muted)]"
                                onClick={cancelEdit}
                              >
                                <X size={13} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                title="Semana Padrão"
                                className="p-1 rounded-sm hover:bg-[var(--bg-elevated)] text-[var(--text-muted)]"
                                onClick={() => setWeekDrawerCargo(cargo)}
                              >
                                <CalendarDays size={13} />
                              </button>
                              <button
                                type="button"
                                title="Editar"
                                className="p-1 rounded-sm hover:bg-[var(--bg-elevated)] text-[var(--text-muted)]"
                                onClick={() => startEdit(cargo)}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                type="button"
                                title="Excluir"
                                className="p-1 rounded-sm hover:bg-red-100/20 text-red-500/70"
                                disabled={deleteMut.isPending}
                                onClick={() => deleteMut.mutate(cargo.id)}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            </>
          )}
        </div>

        {/* Footer note */}
        <div className="px-4 py-1.5 border-t border-[var(--border-default)] shrink-0 bg-[var(--bg-surface)]">
          <span className="text-[10px] text-[var(--text-muted)]">
            DR = FTE Base × Disponibilidade% × (1 − onda mensal%). WIP Máximo: threshold de alerta de sobrecarga.
            T-Shirt sizing (h): P={DEFAULT_SIZING.P} · M={DEFAULT_SIZING.M} · G={DEFAULT_SIZING.G} · GG={DEFAULT_SIZING.GG} (padrão).
          </span>
        </div>
      </div>
    </RequireCapability>
    </AdminConsoleLayout>
  );
};
